import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Form } from '@components/atoms/Form';
import { RoundProfilePicture } from '@components/atoms/RoundProfilePicture';
import { Space } from '@components/atoms/Space';
import styles from './ChangeUnit.module.css';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useRoleChecker } from '@hooks/useRoleChecker';
import { useData } from '@hooks/useData';
import { useToggleUnit } from '@services/useToggleUnit';
import { useNotifications } from '@hooks/useNotifications';

export const ChangeUnit: FC = () => {
    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();

    const translations = {
        measure: t('user-area.measure'),
        measurementLabel: t('user-area.measurement-label'),
        measurementHint: t('user-area.measurement-hint'),
        miles: t('user-area.miles'),
    };

    const { login, user, setUser } = useData();
    const { request: requestUpdateUnit, loading, lastResponseTime, error, success } = useToggleUnit();

    const formRef = useRef<HTMLFormElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [units, setUnits] = useState<string>(user?.data.units ?? 'km');

    const { isRider } = useRoleChecker();

    const handleOnClick = () => {
        setPickerOpen(true); // open picker
    };

    const handleSelectUnit = (selectedUnit: 'km' | 'miles') => {
        setUnits(selectedUnit);
        setPickerOpen(false);

        formRef?.current?.requestSubmit();
    };

    const submitForm: () => Promise<void> = async () => {
        requestUpdateUnit();
    };

    useEffect(() => {
        if (!pickerOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                setPickerOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [pickerOpen]);

    useEffect(() => {
        if (lastResponseTime && success) {
            if (!error) {
                showDefaultNotification({ type: 'success', message: success });
                const updatedUser = user ? { ...user, data: { ...user.data, units } } : user;
                setUser(updatedUser);
            } else {
                showDefaultNotification({ type: 'error', message: error });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    // Render only if user is a Rider
    if (login && !isRider) return null;

    return (
        <Form formRef={formRef} classes="d-flex flex-column" onSubmit={submitForm}>
            <Space spaceBreakpoints={{ xs: { space: 30 } }} />
            <span
                className="d-flex justify-content-between align-items-center"
                onClick={handleOnClick}
                style={{ cursor: 'pointer' }}>
                <Typography fontSize="16" fontWeight="300">
                    <>
                        <Typography fontWeight="300" tagVariant={TypographyTagVariants.span}>
                            <i style={{ marginRight: '5px' }} className="fa-thin fa-ruler"></i>
                        </Typography>
                        {` ${translations.measure}`}
                    </>
                </Typography>
                <Typography color="muted">{units === 'km' ? 'km' : 'mi'}</Typography>
            </span>
            <>
                {pickerOpen && (
                    <div className={styles.overlay}>
                        <div className={styles.pickerContainer} ref={pickerRef}>
                            <RoundProfilePicture
                                icon="fa-thin fa-ruler"
                                labelText={translations.measurementLabel}
                                hintText={translations.measurementHint}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                            <Button
                                label="km"
                                mode={ButtonMode.primary}
                                classes={styles.button}
                                disabled={loading || units === 'km'}
                                onClick={() => handleSelectUnit('km')}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            <Button
                                label={translations.miles}
                                mode={ButtonMode.primary}
                                classes={styles.button}
                                disabled={loading || units === 'miles'}
                                onClick={() => handleSelectUnit('miles')}
                            />
                        </div>
                    </div>
                )}
            </>
        </Form>
    );
};
